import React, { useEffect, useRef } from 'react'
import ResizeObserver from 'rc-resize-observer'

import { saveDataByUid, getDataByUid } from '@/hooks/useRecordOperation/store'
import { addObserveTarget, getFixedTop, getTargetRect, removeObserveTarget, transStyleObjToString } from './utils'

import CollapseWrapper from './components/CollapseWrapper'
import styles from './index.less'

const scorllPxPageAfter = 1

export const scorllPageAfterCollapseClose = () => {
    // 后面的那个是为了兼容打单页面，打单页面只有root，没有real-root
    let scrollPage = document.getElementById('real-root') || document.getElementById('root')
    scrollPage?.scrollBy(0, scorllPxPageAfter)
    scrollPage?.scrollBy(0, -scorllPxPageAfter)
}

function getDefaultTarget() {
    return typeof window !== 'undefined' ? window : null
}

const Affix = ({
    affixPrefixCls = 'ant-affix',
    recordKey,
    className,
    offsetTop,
    target,
    collapsedView,
    children,
    onChange,
    onSizeChange,
    onStateChange,
    onCollapseChange,
    defaultFold,
    ...restProps
}) => {
    const thisRef = useRef({
        collapsed: false,
        userCollapsed: false,
        affixUserCollapsed: false,
        lastAffix: false,
        lastScrollTop: 0,
        scrollDirection: '',
        _methods: {}
    })

    const prependPlaceholderNodeRef = useRef(null)
    const contentNodeRef = useRef(null)
    const placeholderNodeRef = useRef(null)
    const fixedNodeRef = useRef(null)
    const fixNodeContentRef = useRef(null)

    // 注册
    const registerMethods = obj => {
        Object.keys(obj).forEach(v => {
            thisRef.current._methods[v] = obj[v]
        })
    }

    // 卸载
    const unRegisterMethods = keys => {
        keys.forEach(v => {
            delete thisRef.current._methods[v]
        })
    }

    const triggerMethod = (key, ...args) => {
        thisRef.current._methods[key] && thisRef.current._methods[key](...args)
    }

    const onHandle = ({ fold, expand, toggle, getViewRect, getSubviewRect }) => {
        Object.assign(thisRef.current, {
            foldCollapsedView: fold,
            expandCollapsedView: expand,
            toggleCollapsedView: toggle,
            getViewRect,
            getSubviewRect
        })
    }

    const getTargetFunc = () => {
        if (target !== undefined) {
            return target
        }

        return getDefaultTarget
    }

    const getTarget = () => {
        const targetFunc = getTargetFunc()

        return targetFunc()
    }

    const updateState = newState => {
        Object.keys(newState).forEach(key => {
            const value = newState[key]

            if (key === 'collapsed') {
                thisRef.current[key] = value
                thisRef.current?.toggleCollapsedView(value)
                return
            }

            if (key === 'affixStyle') {
                fixedNodeRef.current?.setAttribute('style', transStyleObjToString(value))

                if (value) {
                    fixedNodeRef.current?.classList.add(affixPrefixCls)
                } else {
                    fixedNodeRef.current?.classList.remove(affixPrefixCls)
                }
            }

            if (key === 'prependPlaceholderStyle') {
                prependPlaceholderNodeRef.current?.setAttribute('style', transStyleObjToString(value))
            }

            if (key === 'placeholderStyle') {
                placeholderNodeRef.current?.setAttribute('style', transStyleObjToString(value))
            }

            thisRef.current[key] = value
        })

        const { height } = getTargetRect(fixNodeContentRef.current)

        onStateChange &&
            onStateChange({
                height,
                lastAffix: thisRef.current.lastAffix,
                collapsed: newState.collapsed
            })
    }

    const collapse = e => {
        const { lastAffix } = thisRef.current
        onCollapseChange && onCollapseChange(e)

        if (e) {
            // 收起的时候自动滚动页面，使得虚拟表格重新渲染，不会空白
            scorllPageAfterCollapseClose()
            const cacheKey = `${recordKey}_collapse`

            const record = getDataByUid(cacheKey)

            if (!record) {
                triggerMethod('toggleFieldsConfigTip', true)

                saveDataByUid(cacheKey, 1)
                return
            } else {
                triggerMethod('toggleFieldsConfigTip', false)
            }
        }

        updateState({
            collapsed: e,
            userCollapsed: e,
            affixUserCollapsed: lastAffix && e // 吸顶状态用户点击收起
        })
    }

    // 固定
    const fix = ({
        lastAffix,
        userCollapsed,
        collapsed,
        fixedTop,
        subviewRect,
        fixedNodeWidth,
        fixedNodeHeight,
        scrollDirection,
        affixUserCollapsed
    }) => {
        const newState = {}

        if (!lastAffix || (!collapsed && scrollDirection === 'up')) {
            newState.collapsed = true

            triggerMethod('toggleFieldsConfigTip', false)
        }

        if ((collapsed && !userCollapsed) || (newState.collapsed && !userCollapsed) || affixUserCollapsed) {
            newState.prependPlaceholderStyle = {
                height: subviewRect?.height || 0
            }
        } else {
            newState.prependPlaceholderStyle = undefined
        }

        newState.affixStyle = {
            position: 'fixed',
            top: fixedTop,
            width: fixedNodeWidth,
            height: fixedNodeHeight
        }

        newState.placeholderStyle = {
            width: fixedNodeWidth,
            height: fixedNodeHeight
        }

        newState.lastAffix = true

        updateState(newState)
    }

    // 取消固定
    const unfix = ({ userCollapsed, affixUserCollapsed }) => {
        const newState = {
            affixStyle: undefined,
            placeholderStyle: undefined,
            prependPlaceholderStyle: undefined
        }

        if (!userCollapsed || affixUserCollapsed) {
            newState.collapsed = false
        }

        newState.lastAffix = false

        updateState(newState)
    }

    // 更新位置
    const updatePosition = () => {
        const {
            lastAffix,
            collapsed,
            userCollapsed,
            getViewRect,
            getSubviewRect,
            fixedNodeWidth,
            fixedNodeHeight,
            lastScrollTop,
            affixUserCollapsed
        } = thisRef.current

        const targetNode = getTarget()

        if (!targetNode) {
            return
        }

        const scrollTop = targetNode.scrollTop
        const targetRect = getTargetRect(targetNode)
        const contentReact = getTargetRect(contentNodeRef.current)
        const collapsedViewNodeRect = getViewRect()
        const subviewRect = getSubviewRect()

        const scrollDirection = scrollTop - lastScrollTop > 0 ? 'up' : 'down'
        thisRef.current.scrollDirection = scrollDirection
        thisRef.current.lastScrollTop = scrollTop

        const fixedTop = getFixedTop({
            contentReact,
            targetRect,
            offsetTop,
            collapsed,
            lastAffix,
            userCollapsed,
            subviewRect,
            collapsedViewNodeRect
        })

        if (typeof fixedTop === 'number') {
            fix({
                lastAffix,
                fixedTop,
                contentReact,
                subviewRect,
                collapsed,
                userCollapsed,
                fixedNodeWidth,
                fixedNodeHeight,
                scrollDirection,
                affixUserCollapsed
            })
        } else {
            unfix({
                userCollapsed,
                affixUserCollapsed
            })
        }

        if (onChange && lastAffix !== thisRef.current.lastAffix) {
            const rect = getTargetRect(fixNodeContentRef.current)

            onChange(thisRef.current.lastAffix, rect)
        }
    }

    const handleWrapperResize = ({ width, height }) => {
        Object.assign(thisRef.current, {
            fixedNodeWidth: width
        })

        updatePosition()

        onSizeChange &&
            onSizeChange({
                width,
                height
            })
    }

    const handleContentResize = ({ height }) => {
        Object.assign(thisRef.current, {
            fixedNodeHeight: height
        })

        updatePosition()
    }

    Object.assign(thisRef.current, {
        updatePosition
    })

    useEffect(() => {
        let timeout = null

        const targetFunc = getTargetFunc()
        if (targetFunc) {
            timeout = setTimeout(() => {
                addObserveTarget(targetFunc(), thisRef)

                thisRef.current.updatePosition()
            })
        }

        return () => {
            timeout && clearTimeout(timeout)
            removeObserveTarget(thisRef)
        }
    }, [])

    const clonedView = collapsedView
        ? React.cloneElement(collapsedView, {
              registerMethods,
              unRegisterMethods
          })
        : collapsedView

    return (
        <>
            {collapsedView ? <div ref={prependPlaceholderNodeRef} /> : null}
            <ResizeObserver onResize={handleWrapperResize}>
                <div ref={contentNodeRef} className={className} {...restProps}>
                    <div ref={placeholderNodeRef} aria-hidden="true" />
                    <div ref={fixedNodeRef} className={styles.fixedNode}>
                        <ResizeObserver onResize={handleContentResize}>
                            <div ref={fixNodeContentRef}>
                                {collapsedView ? (
                                    <div className={styles.wrapper}>
                                        <CollapseWrapper
                                            onCollapse={collapse}
                                            defaultFold={defaultFold}
                                            onHandle={onHandle}
                                        >
                                            {clonedView}
                                        </CollapseWrapper>

                                        {children}
                                    </div>
                                ) : (
                                    children
                                )}
                            </div>
                        </ResizeObserver>
                    </div>
                </div>
            </ResizeObserver>
        </>
    )
}

if (process.env.NODE_ENV !== 'production') {
    Affix.displayName = 'Affix'
}

export default Affix
